import type { NodeDefinition } from 'api/node_definitions/endpoints/getNodeDefinition';

import { coerceSelfLoopEdgeType, isLoopRestartConfigurableEdge } from './flowEdgeHelpers';
import { normalizeLoopEdgeRestartDataForSave } from './loopEdgeRestartData';

// Map a freshly-loaded (or freshly-saved) wire record into the editor's working shape: each child node gains the react-flow
// `data` bag (label/handles/submit flags) the Flow canvas renders from, and the placement `description` (from the wire's
// `flow_description`). `zone_name`/`node_definition_name` fall back to values derived from `node_definition_path`/`name` only
// when the wire omits those keys (the `...node` spread otherwise wins). Everything else passes through untouched. Run before
// the record reaches the model so its opaque `child_nodes` already carry the UI shape.
export const mapLoadedToWorking = (loaded: NodeDefinition): NodeDefinition => {
  const childNodes = (loaded.child_nodes as any[]).map((node) => {
    const { id, label, handles, name, zone_name, node_definition_name, node_definition_path } = node;
    const extractedZoneName = node_definition_path?.split('|')[0];
    return {
      id,
      name,
      zone_name: zone_name || extractedZoneName,
      node_definition_name: node_definition_name || name,
      ...node,
      data: {
        label: label || node_definition_name || name,
        handles,
        submit_disabled: node.submit_disabled === true,
        submit_and_forget: node.submit_and_forget === true,
      },
      description: node.flow_description ?? null,
    };
  });
  return { ...loaded, child_nodes: childNodes };
};

// Normalization helpers for the node-definition editor's working state — the react-flow-shaped object the editor holds while
// editing (wire fields plus UI-only state: node `selected`/`dragging`/measured sizes, edge `selected`, a transient
// `selectedNode`, per-wait-event UI `id`s). Dirty tracking uses `normalizeForComparison` via
// `EditableNodeDefinition._persistedSnapshot`. Save uses `normalizeChildEdgesForSave` so self-loops persist as loopback
// even when legacy/API data still has type `default`. Kept pure and framework-free.

/** Drop react-flow's per-node runtime fields (selection, drag, measured geometry) that aren't part of the persisted graph. */
const stripReactFlowInternals = (node: any) => {
  if (!node) return node;
  const { selected, dragging, width, height, measured, positionAbsolute, ...rest } = node;
  return rest;
};

const normalizeOptionalArray = (value: any) => (Array.isArray(value) ? value : []);

/** Drop a wait event's UI-only `id` (used only as a React list key while editing). */
const stripWaitEventUiFields = (event: any) => {
  if (!event) return event;
  const cleaned = { ...event };
  delete cleaned.id;
  return cleaned;
};

/** Drop transient canvas selection (`selectedNode` / `selectedEdgeId`) — never persisted. */
const stripNodeEditorUiState = (nodeData: any) => {
  const cleaned = { ...nodeData };
  delete cleaned.selectedNode;
  delete cleaned.selectedEdgeId;
  return cleaned;
};

export const normalizeWaitEventsForPersistence = (waitEvents: any) => normalizeOptionalArray(waitEvents).map(stripWaitEventUiFields);

const normalizeOptionalText = (value: any) => value ?? '';

/**
 * `config` is opaque and null for node types that carry no config blob (every `flow`, and `conditions`). The state/setState
 * bridge coerces it (`config ?? {}`), so the first bridged write turns a loaded `null` into `{}` — equivalent content, but a
 * different JSON string. Normalize both sides to `{}` so that rewrite isn't mistaken for an edit.
 *
 * NOTE: this asymmetry only exists for `config`. Every other field arrives boundary-validated in the shape the bridge's
 * coercions already produce; `config` is `unknown` by design, so validation can't normalize it.
 */
const normalizeOptionalConfig = (value: any) => value ?? {};

/**
 * Drop react-flow's per-edge `selected` flag and coerce self-loops to `loopback`.
 *
 * Restart options are normalized only on edges that can actually carry them (loopback / error-loopback / self-loop). A
 * plain forward edge whose `data` holds nothing but routing `waypoints` must NOT gain `auto_restart_*` keys: the backend
 * reads any non-null edge `data` as "configured", so writing restart fields onto an edge that can never loop states a
 * policy the edge does not have.
 */
const stripReactFlowEdgeInternals = (edge: any) => {
  if (!edge) return edge;
  const { selected, ...rest } = edge;
  const coerced = { ...rest, type: coerceSelfLoopEdgeType(rest) };
  if (rest.data == null || !isLoopRestartConfigurableEdge(coerced)) {
    return coerced;
  }
  return { ...coerced, data: normalizeLoopEdgeRestartDataForSave(rest.data) };
};

/** Edges for the save payload: strip RF UI flags and coerce self-loops to `loopback`. */
export const normalizeChildEdgesForSave = (edges: readonly unknown[] | undefined | null) => (edges ?? []).map(stripReactFlowEdgeInternals);

/**
 * Project the working state down to its persisted, comparison-stable content: UI-only fields removed and the collections
 * normalized. Two working states that differ only in react-flow UI noise (selection, drag position jitter) compare equal,
 * so the editor's dirty flag reflects real edits.
 */
export const normalizeForComparison = (nodeData: any) => {
  if (!nodeData) return nodeData;
  const persistedNodeData = stripNodeEditorUiState(nodeData);

  return {
    ...persistedNodeData,
    config: normalizeOptionalConfig(nodeData.config),
    retention_policies: normalizeOptionalArray(nodeData.retention_policies),
    wait_events: normalizeWaitEventsForPersistence(nodeData.wait_events),
    documentation: normalizeOptionalText(nodeData.documentation),
    child_nodes: nodeData.child_nodes?.map(stripReactFlowInternals) || [],
    child_edges: nodeData.child_edges?.map(stripReactFlowEdgeInternals) || [],
  };
};
